import FW from '../fw/fw';
import Env from '../fw/env';
import Logger from '../fw/logger';
import LocalStorage from '../fw/local-storage';
import CuePoints from '../modules/cue-points';


export default class EventsManagement {

  #rmp;
  #playerWaiting = false;
  #loopEventFired = false;
  #firstTimeupdate = false;
  #currentTimeBeforeSeek = -1;
  #currentTimeBeforeAd = -1;
  #unstalledBufferFn = null;
  #dvrCurrentTime = -1;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  get firstTimeupdate() {
    return this.#firstTimeupdate;
  }

  get currentTimeBeforeSeek() {
    return this.#currentTimeBeforeSeek;
  }

  get currentTimeBeforeAd() {
    return this.#currentTimeBeforeAd;
  }

  get dvrCurrentTime() {
    return this.#dvrCurrentTime;
  }

  static checkInView(container, element) {
    //Get container properties
    let cTop = container.scrollTop;
    let cBottom = cTop + container.clientHeight;
    //Get element properties
    let eTop = element.offsetTop + 60;
    let eBottom = eTop + element.clientHeight;
    //Check if in view    
    return (eTop >= cTop && eBottom <= cBottom);
  }

  #updateProgress() {
    if (this.#rmp.isLive) {
      return;
    }
    const video = this.#rmp.dom.video;
    const duration = FW.getMediaDuration(video);
    let valueProgress = 0;
    const timeBuffered = video.buffered;
    const length = video.buffered.length;
    const currentTime = FW.getMediaCurrentTime(video);
    let index = 0;
    // try catch because in some rare cases 
    // timeBuffered.start or timeBuffered.end may throw
    try {
      for (let j = length - 1; j >= 0; j--) {
        if (currentTime >= timeBuffered.start(j)) {
          index = j;
          break;
        }
      }
      if (length > 0 && FW.isFiniteNumber(duration)) {
        valueProgress = (timeBuffered.end(index) / duration) * 100;
      }
    } catch (warning) {
      console.warn(warning);
      return;
    }
    if (FW.isFiniteNumber(valueProgress) && this.#rmp.dom.loaded) {
      this.#rmp.dom.loaded.style.width = valueProgress + '%';
    }
  }

  #stalledBuffer() {
    this.#rmp.rmpUIManagement.showLoadingSpin();
    this.#rmp.dispatch('waiting');
    this.#rmp.dispatch('bufferstalled');
    this.wireUnstalledBufferEvents();
  }

  #removeCurrentTranscriptsItem() {
    const transcriptsSideMenu = this.#rmp.dom.transcriptsContainer.querySelector('.rmp-transcripts-side-menu');
    if (transcriptsSideMenu) {
      const transcriptsItems = transcriptsSideMenu.querySelectorAll('.rmp-transcripts-item');
      if (transcriptsItems.length > 0) {
        for (let i = 0, len = transcriptsItems.length; i < len; i++) {
          if (transcriptsItems[i].classList.contains('rmp-transcripts-item-active')) {
            transcriptsItems[i].classList.remove('rmp-transcripts-item-active');
            break;
          }
        }
      }
    }
  }

  durationchangeCallback() {
    const duration = this.#rmp.duration;
    const currentTime = this.#rmp.currentTime;

    // set live UI for native HLS
    if (this.#rmp.readingHls && this.#rmp.detectLive) {
      if (typeof this.#rmp.dom.video.duration === 'number' && Math.abs(this.#rmp.dom.video.duration) === Infinity) {
        Logger.print('info', 'native HLS stream is a live stream: true');
        this.#rmp.rmpUIManagement.prepareLiveUI();
      } else {
        Logger.print('info', 'native HLS stream is a live stream: false');
        this.#rmp.rmpSeekManagement.appendSeekBar();
      }
      this.#rmp.detectLive = false;
    }

    // set live UI for audio only Icecast
    if (this.#rmp.audioOnlyIcecast && (this.#rmp.audioOnly || this.#rmp.audioOnlyUseVideoLayout) &&
      this.#rmp.detectLive) {
      if (typeof this.#rmp.dom.video.duration === 'number' && Math.abs(this.#rmp.dom.video.duration) === Infinity) {
        Logger.print('info', 'Icecast live detected');
        this.#rmp.rmpUIManagement.prepareLiveUI();
      } else {
        Logger.print('info', 'Icecast VOD detected');
        this.#rmp.rmpSeekManagement.appendSeekBar();
      }
      this.#rmp.detectLive = false;
    }

    // in all cases we show timeSeparator here 
    FW.showInline(this.#rmp.dom.timeSeparator);

    if (this.#rmp.isVod && duration > -1 && currentTime > -1) {
      this.#rmp.rmpUtils.updateDurationText(duration, currentTime);
    }

    if (this.#rmp.isDvr && this.#rmp.isDvrLiveMode) {
      FW.setText(this.#rmp.dom.timeElapsedText, this.#rmp.labels.hint.live);
    }

    if (this.#rmp.firstDurationChange) {
      this.#rmp.firstDurationChange = false;
      this.#rmp.dom.container.classList.remove('rmp-wait-for-duration');
      if (this.#rmp.rmpAdScheduler) {
        this.#rmp.rmpAdScheduler.init(duration);
      }
    }

    this.#rmp.dispatch('durationchange');

    Logger.print('info', 'new duration (ms) is: ' + duration);
  }

  loadedmetadataCallback() {
    if (!this.#rmp.hasLoadedMetadata) {
      if (this.#rmp.hasPlaylist) {
        this.#rmp.dispatch('playlistitemloaded');
      }
      if (this.#rmp.hasRelated) {
        this.#rmp.dispatch('relateditemloaded');
      }
      if (this.#rmp.cuePoints.length > 0 && this.#rmp.isVod) {
        this.#rmp.rmpCuePoints = new CuePoints(this.#rmp);
        this.#rmp.rmpCuePoints.init();
      }
      this.#rmp.hasLoadedMetadata = true;
    }
    this.#rmp.dispatch('loadedmetadata');
  }

  wireUnstalledBufferEvents() {
    this.#unstalledBufferFn = this.unstalledCallback.bind(this);
    this.#rmp.dom.video.addEventListener('adstarted', this.#unstalledBufferFn);
    this.#rmp.dom.video.addEventListener('timeupdate', this.#unstalledBufferFn);
    this.#rmp.dom.video.addEventListener('playing', this.#unstalledBufferFn);
    this.#rmp.dom.video.addEventListener('canplay', this.#unstalledBufferFn);
    this.#rmp.dom.video.addEventListener('canplaythrough', this.#unstalledBufferFn);
  }

  unwireUnstalledBufferEvents() {
    this.#rmp.dom.video.removeEventListener('adstarted', this.#unstalledBufferFn);
    this.#rmp.dom.video.removeEventListener('timeupdate', this.#unstalledBufferFn);
    this.#rmp.dom.video.removeEventListener('playing', this.#unstalledBufferFn);
    this.#rmp.dom.video.removeEventListener('canplay', this.#unstalledBufferFn);
    this.#rmp.dom.video.removeEventListener('canplaythrough', this.#unstalledBufferFn);
  }

  unstalledCallback() {
    if (this.#rmp.readingShaka) {
      this.#playerWaiting = false;
    }
    this.#rmp.rmpUIManagement.hideLoadingSpin();
    this.unwireUnstalledBufferEvents();
    this.#rmp.dispatch('buffernotstalledanymore');
  }

  waitingCallback() {
    if (this.#rmp.readingShaka) {
      if (!this.#playerWaiting) {
        this.#playerWaiting = true;
        this.#stalledBuffer();
      }
    } else {
      this.#stalledBuffer();
    }
  }

  endedListCallback() {
    if (this.#rmp.hasRelated) {
      const currentRelatedIndex = this.#rmp.rmpRelated.currentRelatedIndex;
      if (typeof this.#rmp.rmpRelated.relatedList[currentRelatedIndex + 1] === 'undefined') {
        this.#rmp.dispatch('relatedcomplete');
      }
      if (this.#rmp.relatedUpNextAutoplay) {
        if (this.#rmp.relatedEndedLoop ||
          (!this.#rmp.relatedEndedLoop &&
            typeof this.#rmp.rmpRelated.relatedList[currentRelatedIndex + 1] !== 'undefined')) {
          this.#rmp.dom.relatedSkipPreview.classList.remove('rmp-force-show');
          this.#rmp.rmpRelated.loadRelatedItem(currentRelatedIndex + 1);
        }
      } else {
        this.#rmp.setModuleOverlayVisible('related', true);
      }
    }
    if (this.#rmp.hasPlaylist) {
      const currentPlaylistIndex = this.#rmp.rmpPlaylist.currentPlaylistIndex;
      const playlistList = this.#rmp.rmpPlaylist.playlistList;
      if (typeof playlistList[currentPlaylistIndex + 1] === 'undefined') {
        this.#rmp.dispatch('playlistcomplete');
      }
      if (this.#rmp.playlistUpNextAutoplay) {
        if (this.#rmp.playlistEndedLoop ||
          (!this.#rmp.playlistEndedLoop &&
            typeof playlistList[currentPlaylistIndex + 1] !== 'undefined')) {
          this.#rmp.rmpPlaylist.loadPlaylistItem(currentPlaylistIndex + 1);
        }
      }
    }
  }

  endedCallback() {
    if (!this.#rmp.loopConfiguration) {
      if (this.#rmp.ads) {
        // disable postroll VMAP on iOS with IMA >> content does not resore 
        if (!Env.isIos[0]) {
          if (this.#rmp.rmpAdsIma) {
            this.#rmp.contentCompleteCalled = true;
            try {
              this.#rmp.rmpAdsIma.adsLoader.contentComplete();
            } catch (warning) {
              console.warn(warning);
            }
          }
        }
        // VMAP or ad-scheduler - clear markers from control bar if adTagReloadOnEnded === false
        if (!this.#rmp.adTagReloadOnEnded) {
          this.#rmp.rmpAdsHelpers.clearAdMarkers();
        }
      }
      if (this.#rmp.dom.poster) {
        if (this.#rmp.contentMetadata.endOfVideoPoster.length > 0) {
          this.#rmp.endOfVideoPoster = FW.getPoster(this.#rmp.contentMetadata.endOfVideoPoster);
        }
        Logger.print('metadata',
          `validated endOfVideoPoster is ${this.#rmp.endOfVideoPoster} for devicePixelRatio ${Env.devicePixelRatio}`);
        if (this.#rmp.endOfVideoPoster !== '' && this.#rmp.endOfVideoPoster !== this.#rmp.posterUri) {
          this.#rmp.poster = this.#rmp.endOfVideoPoster;
        }
        this.#rmp.showPoster();
      }
      if (this.#rmp.isMobile) {
        this.#rmp.dom.centralMobileUIIcon.classList.remove('rmp-i-pause', 'rmp-i-play');
        this.#rmp.dom.centralMobileUIIcon.classList.add('rmp-i-replay');
      } else {
        this.#rmp.dom.overlayButtonIcon.classList.replace('rmp-i-play', 'rmp-i-replay');
      }
    }
    this.endedListCallback();
    this.#rmp.dispatch('ended');
  }

  attachAuxiliaryEvents() {
    const events = [
      'loadstart',
      'loadeddata',
      'progress',
      'canplay',
      'canplaythrough',
      'ratechange',
      'seeking',
      'seeked',
      'playing'
    ];
    events.forEach(value => {
      this.#rmp.dom.video.addEventListener(value, event => {
        let eventType = event.type;
        if (eventType) {
          switch (eventType) {
            case 'playing':
            case 'seeked':
              this.#rmp.rmpUIManagement.hideLoadingSpin();
              break;
            case 'loadeddata':
            case 'progress':
              this.#updateProgress();
              break;
            default:
              break;
          }
          this.#rmp.dispatch(eventType);
        }
      });
    });
  }

  pauseCallback() {
    if (this.#rmp.googleCast && this.#rmp.pauseRequestedByLoadCastMedia) {
      this.#rmp.pauseRequestedByLoadCastMedia = false;
      return;
    }
    const playLabel = this.#rmp.labels.hint.play;
    this.#rmp.dom.playPause.classList.replace('rmp-i-pause', 'rmp-i-play');
    this.#rmp.dom.playPause.setAttribute(
      'aria-label',
      playLabel + ' ' + this.#rmp.labels.hint.keyboardShortcut + ' k'
    );
    FW.setText(this.#rmp.dom.playPauseHint, playLabel + ' (k)');
    if (this.#rmp.isMobile) {
      this.#rmp.dom.centralMobileUIIcon.classList.replace('rmp-i-pause', 'rmp-i-play');
      this.#rmp.dom.centralMobileUI.setAttribute('aria-label', playLabel);
    }
    this.#rmp.rmpUIManagement.showCentralPlay();
    this.#rmp.rmpModuleManagement.updatePlayPauseUI(true);
    this.#rmp.dispatch('pause');
  }

  removeReplayIcon() {
    if (this.#rmp.isMobile) {
      if (this.#rmp.dom.centralMobileUIIcon.classList.contains('rmp-i-replay')) {
        this.#rmp.dom.centralMobileUIIcon.classList.replace('rmp-i-replay', 'rmp-i-pause');
      }
    } else {
      if (this.#rmp.dom.overlayButtonIcon.classList.contains('rmp-i-replay')) {
        this.#rmp.dom.overlayButtonIcon.classList.replace('rmp-i-replay', 'rmp-i-play');
      }
    }
  }

  playCallback() {
    const pauseLabel = this.#rmp.labels.hint.pause;
    this.#rmp.dom.playPause.classList.replace('rmp-i-play', 'rmp-i-pause');
    this.#rmp.dom.playPause.setAttribute(
      'aria-label',
      pauseLabel + ' ' + this.#rmp.labels.hint.keyboardShortcut + ' k'
    );
    FW.setText(this.#rmp.dom.playPauseHint, pauseLabel + ' (k)');
    if (this.#rmp.isMobile) {
      this.#rmp.dom.centralMobileUIIcon.classList.replace('rmp-i-play', 'rmp-i-pause');
      this.#rmp.dom.centralMobileUI.setAttribute('aria-label', pauseLabel);
    }
    this.#rmp.rmpUIManagement.hideCentralPlay();
    // remove replay icon in case it is here
    this.removeReplayIcon();
    this.#rmp.rmpModuleManagement.updatePlayPauseUI(false);
    this.#rmp.hidePoster();
    this.#rmp.dispatch('play');
  }

  timeupdateCallback() {
    // on Apple devices when no MSE >> do not send timeupdate when ad on stage for rmp-vast
    if (this.#rmp.ads && this.#rmp.adParser === 'rmp-vast' && this.#rmp.readingPDorNative && this.#rmp.adOnStage) {
      return;
    }
    // write rmpQuality
    if (this.#rmp.rememberQuality && LocalStorage.available(this.#rmp.allowLocalStorage) &&
      !this.#rmp.linearAdOnStage) {
      const currentQuality = this.#rmp.quality;
      if (currentQuality !== -2) {
        LocalStorage.writeQuality(currentQuality);
      }
    }
    if (!this.#firstTimeupdate) {
      this.#firstTimeupdate = true;
    }
    // currentTimeBeforeSeek
    const currentTimeBefore = this.#rmp.currentTime;
    if (!this.#rmp.linearAdOnStage) {
      this.#currentTimeBeforeAd = currentTimeBefore;
    }
    this.#currentTimeBeforeSeek = currentTimeBefore;
    if (this.#rmp.isLive || (this.#rmp.isDvr && this.#rmp.isDvrLiveMode)) {
      this.#rmp.dispatch('timeupdate');
      return;
    }
    // write rmpCurrentTime for VOD streams
    if (this.#rmp.isVod && this.#currentTimeBeforeSeek > 0 && this.#rmp.rememberCurrentTime &&
      LocalStorage.available(this.#rmp.allowLocalStorage) && !this.#rmp.linearAdOnStage) {
      LocalStorage.writeCurrentTime(this.#currentTimeBeforeSeek, this.#rmp.contentMetadata.id);
    }
    if (this.#rmp.rmpInitManagement.hasTranscripts && this.#rmp.dom.transcriptsContainer) {
      const transcriptsSideMenu = this.#rmp.dom.transcriptsContainer.querySelector('.rmp-transcripts-side-menu');
      if (transcriptsSideMenu) {
        const transcriptsItems = transcriptsSideMenu.querySelectorAll('.rmp-transcripts-item');
        if (transcriptsItems.length > 0) {
          for (let i = 0, len = transcriptsItems.length; i < len; i++) {
            if (i === 0) {
              const start = transcriptsItems[i].getAttribute('data-start-time');
              const startMS = FW.dateToSeconds(start) * 1000;
              if (startMS > this.#currentTimeBeforeSeek) {
                this.#removeCurrentTranscriptsItem();
                break;
              }
            }
            const end = transcriptsItems[i].getAttribute('data-end-time');
            const endMS = FW.dateToSeconds(end) * 1000;
            if (i === len - 1) {
              if (this.#currentTimeBeforeSeek > endMS) {
                this.#removeCurrentTranscriptsItem();
                break;
              }
            }
            if (endMS > this.#currentTimeBeforeSeek) {
              if (!transcriptsItems[i].classList.contains('rmp-transcripts-item-active')) {
                if (!EventsManagement.checkInView(transcriptsSideMenu, transcriptsItems[i])) {
                  transcriptsSideMenu.scrollTop = transcriptsItems[i].offsetTop - 1;
                }
                this.#removeCurrentTranscriptsItem();
                transcriptsItems[i].classList.add('rmp-transcripts-item-active');
              }
              break;
            }
          }
        }
      }
    }
    if (this.#rmp.dom.video && this.#rmp.dom.current && this.#rmp.dom.handle) {
      if (!this.#rmp.rmpSeekManagement.draggingTimeCursor && !this.#rmp.rmpSeekManagement.touchDown) {
        let duration;
        let currentTime;
        let seekRange, start, end;
        let dvrShakaValue;
        if (this.#rmp.isLive) {
          duration = -1;
          currentTime = -1;
        } else if (this.#rmp.googleCastConnected) {
          const castCurrentTime = this.#rmp.currentTime;
          if (castCurrentTime > -1) {
            currentTime = castCurrentTime / 1000;
          } else {
            currentTime = castCurrentTime;
          }
          duration = this.#rmp.duration / 1000;
        } else {
          duration = FW.getMediaDuration(this.#rmp.dom.video);
          currentTime = FW.getMediaCurrentTime(this.#rmp.dom.video);
          if (this.#rmp.isDvr) {
            if (this.#rmp.rmpHlsJS) {
              const slide = duration - this.#rmp.rmpHlsJS.hlsJSLevelDuration;
              duration = this.#rmp.rmpHlsJS.hlsJSLevelDuration;
              currentTime = currentTime - slide;
            } else if (this.#rmp.readingShaka) {
              seekRange = this.#rmp.shakaPlayer.seekRange();
              start = seekRange.start;
              end = seekRange.end;
              duration = end;
              if (FW.isFiniteNumber(end) && FW.isFiniteNumber(start) && end > 0 && start > 0) {
                dvrShakaValue = 1 - ((end - currentTime) / (end - start));
              }
            }
          }
        }
        if (FW.isFiniteNumber(duration) && duration > 0 && FW.isFiniteNumber(currentTime) && currentTime >= 0) {
          let value;
          if (this.#rmp.isDvr && this.#rmp.readingShaka) {
            value = dvrShakaValue * 100;
          } else {
            value = (100 / duration) * currentTime;
          }
          this.#rmp.dom.current.style.width = value + '%';
          this.#rmp.dom.handle.style.left = value + '%';
          let textValue, element;
          if (this.#rmp.isDvr && this.#rmp.dom.durationText) {
            element = this.#rmp.dom.durationText;
            textValue = '-' + FW.readableTime(duration - currentTime);
            if (this.#rmp.readingHlsJS) {
              this.#dvrCurrentTime = currentTime;
            } else if (this.#rmp.readingShaka) {
              this.#dvrCurrentTime = currentTime - start;
            }
          } else if (this.#rmp.dom.timeElapsedText) {
            element = this.#rmp.dom.timeElapsedText;
            textValue = FW.readableTime(currentTime);
            const duration = this.#rmp.duration;
            if (duration > -1) {
              this.#rmp.rmpUtils.updateDurationText(duration, this.#rmp.currentTime);
            }
          }
          FW.setText(element, textValue);
        }
      }
    }
    let currentTime = this.#currentTimeBeforeSeek;
    let duration = this.#rmp.duration;
    if (this.#rmp.isLive) {
      duration = -1;
      currentTime = -1;
    }
    // update aria label
    if (duration > -1 && currentTime > -1) {
      const valuenow = (FW.precisionRound((currentTime * 100) / duration, 2)).toString();
      const valuetext = FW.readableTime(currentTime / 1000) + ' / ' + FW.readableTime(duration / 1000);
      if (DEBUG) {
        if (this.#rmp.debugLevel === 2) {
          Logger.print('accessibility', 'aria-valuenow for seek bar is: ' + valuenow);
          Logger.print('accessibility', 'aria-valuetext for seek bar is: ' + valuetext);
        }
      }
      this.#rmp.dom.seekBar.setAttribute('aria-valuenow', valuenow);
      this.#rmp.dom.seekBar.setAttribute('aria-valuetext', valuetext);
    }
    // show/hide skipPreview near the end at relatedUpNextOffset
    if (!this.#rmp.googleCastConnected && this.#rmp.hasRelated && this.#rmp.relatedUpNextOffset > 0) {
      if (currentTime > -1 && duration > -1) {
        const diff = duration - currentTime;
        if (diff <= this.#rmp.relatedUpNextOffset * 1000) {
          if (this.#rmp.linearAdOnStage) {
            // this is for iOS where timeupdate fires when ad is on stage
            this.#rmp.dom.relatedSkipPreview.classList.remove('rmp-force-show');
          } else {
            this.#rmp.rmpUIManagement.showChrome();
            this.#rmp.dom.relatedSkipPreview.classList.add('rmp-force-show');
          }
        } else {
          this.#rmp.dom.relatedSkipPreview.classList.remove('rmp-force-show');
        }
      }
    }
    if (this.#rmp.loop && this.#rmp.isVod && currentTime > -1 && duration > -1) {
      const diff = duration - currentTime;
      if ((diff < 300) && !this.#loopEventFired) {
        this.#loopEventFired = true;
        window.setTimeout(() => {
          this.#loopEventFired = false;
        }, 301);
        this.#rmp.dispatch('loop');
      }
    }
    this.#rmp.dispatch('timeupdate');
  }

}
